/**
 * The corner helper on an assistant page (ADR 0061).
 *
 * Every page that is "the data" carries the assistant's chat in the
 * corner: it receives what is on screen as `page_context` on each turn,
 * and what its tools write, the page reloads. This is that loop, once —
 * the two pages that had it each carried their own copy.
 *
 * The page decides two things: what the helper is told (`pageContext`)
 * and what to do when a turn ends (`afterTurn`, usually a refresh). A
 * page whose record IS a conversation (the bid) hands the conversation
 * id in; a page without one (the quote) lets the helper own it.
 */
import { computed, reactive, ref } from "vue";

import { sendChat } from "../api/chat";
import { createConversation, uploadConversationFile } from "../api/conversations";
import type { ChatEvent, ChatTurn } from "../types/chat";

export interface HelperChatOptions {
  assistantSlug: string;
  devUser: () => string;
  /** The page's conversation on the record. Omit to let the helper own one. */
  conversationId?: () => number | null;
  /** What the helper is told about the screen, each turn. Keep it short. */
  pageContext: () => string;
  /** Page-specific reactions to a streamed event (a tool wrote something). */
  onEvent?: (evt: ChatEvent, turn: ChatTurn) => void;
  /** After every turn, success or failure — the page usually refreshes. */
  afterTurn?: (turn: ChatTurn) => void | Promise<void>;
  /** Show an attach button: files go into the helper's conversation, where
   * the assistant's `read_uploaded_doc` finds them. Only for a helper that
   * owns its conversation, and only when the assistant has that tool. */
  uploads?: boolean;
}

export function useHelperChat(opts: HelperChatOptions) {
  const open = ref(false);
  const input = ref("");
  const busy = ref(false);
  const turns = reactive<ChatTurn[]>([]);
  const owned = ref<number | null>(null);
  let seq = 0;

  const conversationId = computed<number | null>(() =>
    opts.conversationId ? opts.conversationId() : owned.value,
  );

  async function send(): Promise<void> {
    const message = input.value.trim();
    if (!message || busy.value) return;
    if (opts.conversationId && opts.conversationId() === null) return;
    input.value = "";
    turns.push({ id: `u${++seq}`, role: "user", content: message });
    turns.push({ id: `a${++seq}`, role: "assistant", content: "", streaming: true });
    const turn = turns[turns.length - 1];
    busy.value = true;
    try {
      const started = await sendChat({
        message,
        devUser: opts.devUser(),
        assistantSlug: opts.assistantSlug,
        conversationId: conversationId.value ?? undefined,
        pageContext: opts.pageContext(),
      });
      if (!opts.conversationId && started.conversationId !== null) {
        owned.value = started.conversationId;
      }
      for await (const evt of started.events) {
        if (evt.type === "delta") turn.content += evt.content;
        else if (evt.type === "retrieval") turn.retrieval = evt;
        else if (evt.type === "tool_call") {
          if (!turn.tools) turn.tools = [];
          turn.tools.push({ id: evt.id, name: evt.name, arguments: evt.arguments });
        } else if (evt.type === "tool_result") {
          const rt = (turn.tools ?? []).find((t) => t.id === evt.id);
          if (rt) rt.result = evt;
        } else if (evt.type === "workflow") turn.matchedWorkflows = evt.matched;
        else if (evt.type === "error") turn.error = evt.message;
        else if (evt.type === "persisted") {
          const mid = (evt as { message_id?: unknown }).message_id;
          if (typeof mid === "number") turn.messageId = mid;
        }
        opts.onEvent?.(evt, turn);
        if (evt.type === "done") break;
      }
    } catch (e) {
      turn.error = e instanceof Error ? e.message : String(e);
    } finally {
      turn.streaming = false;
      busy.value = false;
    }
    await opts.afterTurn?.(turn);
  }

  // ── Attachments ──────────────────────────────────────────────────────
  const uploadsEnabled = !!opts.uploads && !opts.conversationId;
  const attached = ref<string[]>([]);
  const uploadStatus = ref("");

  /** Attach files. Attaching the document you want to ask about is a
   * natural first move, so the conversation is created on demand. */
  async function attach(files: File[]): Promise<void> {
    if (!uploadsEnabled || !files.length || busy.value) return;
    busy.value = true;
    try {
      if (owned.value === null) {
        uploadStatus.value = "Starter en samtale til filen…";
        const conv = await createConversation(opts.assistantSlug, opts.devUser() || undefined);
        if (conv === null) {
          uploadStatus.value = "Kunne ikke starte en samtale til filen. Prøv igen.";
          return;
        }
        owned.value = conv.id;
      }
      for (const file of files) {
        uploadStatus.value = `Uploader ${file.name}…`;
        const res = await uploadConversationFile(owned.value, file, opts.devUser() || undefined);
        if (res.index_error) {
          uploadStatus.value = `${file.name} er gemt, men kunne ikke læses: ${res.index_error}`;
          return;
        }
        attached.value = [...attached.value, res.filename ?? file.name];
      }
      uploadStatus.value = "";
    } catch (e) {
      uploadStatus.value = `Upload fejlede: ${e instanceof Error ? e.message : String(e)}`;
    } finally {
      busy.value = false;
    }
  }

  /** A page button's request: open the helper, put the text in, send. */
  function ask(text: string): void {
    open.value = true;
    input.value = text;
    void send();
  }

  /** Leaving the record: forget the transcript and an owned conversation. */
  function reset(): void {
    turns.splice(0);
    owned.value = null;
    attached.value = [];
    uploadStatus.value = "";
  }

  return {
    open, input, busy, turns, conversationId, send, ask, reset,
    uploadsEnabled, attached, uploadStatus, attach,
  };
}

export type HelperChat = ReturnType<typeof useHelperChat>;
